"use client";

import { MapToDocumentListItems } from "@/src/utils/database-helpers/mapper/document-mapper";
import ArticleList from "@/src/_types/Article/article-list";
import IssuePageType from "@/src/_types/IssuePage/issue-page-type";
import ErrorTemplate from "@/src/_types/errorTemplate";
import { Box, Typography } from "@mui/material";
import ActionButton from "@/src/components/common/broadsheet/action-button";
import Kicker from "@/src/components/common/broadsheet/kicker";
import {
  FONT_DISPLAY,
  FONT_TEXT,
  HEADING_SIZE,
  RAIL_RIGHT,
  SHEET_GUTTER,
} from "@/src/components/common/broadsheet/tokens";
import { issueLabel } from "@/src/utils/textTransform";
import { getXmlIssueDownloadLink } from "@/src/utils/journal-directory-helper";
import Articles from "@/src/components/journal-site/common/articles/articles";
import CardWidget from "@/src/components/journal-site/common/card-widget/card-widget";
import Page from "@/src/_types/Page/page";

const EmptyIssueNotice = ({ error }: { error: ErrorTemplate }) => (
  <Box sx={{ py: { xs: 1, md: 2 } }}>
    {error.header ? <Kicker>{error.header}</Kicker> : null}
    <Typography
      component="h2"
      sx={{
        m: 0,
        mt: 0.5,
        fontFamily: FONT_DISPLAY,
        fontWeight: 700,
        fontSize: HEADING_SIZE.page,
        lineHeight: 1.15,
        letterSpacing: "0.01em",
        color: "custom.ink",
      }}
    >
      {error.title ?? "No articles found"}
    </Typography>
    <Typography
      component="p"
      sx={{
        m: 0,
        mt: 0.75,
        maxWidth: "36rem",
        fontFamily: FONT_TEXT,
        fontSize: "0.9375rem",
        lineHeight: 1.6,
        color: "custom.inkMuted",
      }}
    >
      {error.message}
    </Typography>
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1.25 }}>
      {error.publishJournalLink ? (
        <ActionButton href={error.publishJournalLink} variant="solid">
          Return home
        </ActionButton>
      ) : null}
      {error.supportLink ? (
        <ActionButton href={error.supportLink} variant="outline" external>
          Contact support
        </ActionButton>
      ) : null}
    </Box>
  </Box>
);

export default function IssueArticles({
  journal,
  issue,
  documents,
  widgets,
  error,
  title,
}: {
  journal: any;
  issue: any;
  documents: any;
  widgets: Page[];
  error: ErrorTemplate;
  title?: string;
}) {
  const issueDocuments: ArticleList[] = MapToDocumentListItems({
    journal,
    documents,
  });

  issueDocuments.sort((a, b) => {
    return a.display_order - b.display_order;
  });

  const xmlDownloadLink =
    journal.services?.issue_isc_xml?.status && issue
      ? getXmlIssueDownloadLink(journal, issue.id)
      : null;

  const issueData: IssuePageType = {
    articles: issueDocuments,
    caption: issue?.caption,
    volume: issue?.volume_name ?? "",
    date: issue?.formatted_publish_date,
    xmlLink: xmlDownloadLink,
  };

  const derivedTitle = issueLabel(issue?.volume_name, issue?.caption);

  return (
    <Box
      sx={{
        px: SHEET_GUTTER,
        pt: { xs: 1, md: 1.25 },
        pb: { xs: 1.5, md: 2.5 },
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: `1fr ${RAIL_RIGHT}` },
        columnGap: 2.5,
        rowGap: 1.5,
        alignItems: "start",
        "& > *": { minWidth: 0 },
      }}
    >
      <Box>
        {issueData.articles?.length && issueData.articles.length > 0 ? (
          <Articles
            title={title ?? derivedTitle}
            articles={issueData.articles}
            date={issueData.date}
            volume={issueData.volume}
            caption={issueData.caption}
            xmlLink={issueData.xmlLink}
            desc={issueData.desc}
            headingSize="page"
          />
        ) : (
          <EmptyIssueNotice error={error} />
        )}
      </Box>

      {widgets && widgets.length > 0 ? (
        <Box
          component="aside"
          aria-label="About this journal"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
            pl: { lg: 1.25 },
            borderLeft: { lg: "1px solid" },
            borderColor: { lg: "custom.ruleHairline" },
          }}
        >
          {widgets.map((value, index) => (
            <CardWidget value={value} key={index} />
          ))}
        </Box>
      ) : null}
    </Box>
  );
}
